import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import NotFound from '@/NotFound.vue'
import store from '@/store'
import LoginService from '@/LoginService';
import { UserMessageCommand } from '@/UserMessageCommand';
import { CheckSubscriptionBySessionIdCommand } from '@/CheckSubscriptionBySessionIdCommand';
import ConfirmAccountCommand from './ConfirmAccountCommand';
import { docPages } from './DocPages';
import LinkRideWithGpsCommand from './LinkRideWithGpsCommand';
import LinkWahooCommand from './LinkWahooCommand';
import PolicyService from './PolicyService';
import { RideWithGpsMigrationPath } from './RideWithGpsService';
import WahooService from './WahooService';
import { consumeLoginReturnUrl, internalLoginReturnUrl } from './LoginReturnUrl';

Vue.use(VueRouter)

const mobileAppHostname = 'mobile.gpxplanner.app'

function isMobileAppHost() {
    return window.location.hostname === mobileAppHostname
}

const appRoutes: Array<RouteConfig> = [
    {
        path: '/',
        name: 'Home',
        component: () => isMobileAppHost()
            ? import(/* webpackChunkName: "mobile-app" */ './MobileAppLanding.vue')
            : import(/* webpackChunkName: "home" */ './Home.vue'),
        meta: { doLogin: !isMobileAppHost(), allowAnonymous: isMobileAppHost() },
        async beforeEnter(to, from, next) {
            next()
        }
    },
    {
        path: '/link-subscription',
        name: 'Link subscription with mobile app',
        component: () => import(/* webpackChunkName: "mobile-app" */ './MobileAppLanding.vue'),
        meta: { allowAnonymous: true }
    },
    {
        path: '/setpassword',
        name: 'Set password',
        component: () => import(/* webpackChunkName: "resetpassword" */ './SetPassword.vue'),
        meta: { titleSuffix: ' - Set new password' }
    },
    {
        path: '/confirmaccount',
        meta: { allowAnonymous: true },
        beforeEnter: async function(to, from, next) {
            const code = to.query.code
            if(code) {
                await store.commit('pushCommand', new ConfirmAccountCommand(code as string))
            } else {
                await store.commit('pushCommand', new UserMessageCommand('Confirmation code invalid', 'error', false))
            }
            next('/')
        }
    },
    {
        path: '/setpasswordsuccess',
        meta: { allowAnonymous: true },
        beforeEnter: async function (to, from, next) {
            await store.commit('pushCommand', new UserMessageCommand('Password has been updated. You can now log in with your new password.', 'success', true))
            next('/')
        }
    },
    {
        path: '/subscribesuccess',
        beforeEnter: async function(to, from, next) {
            const sessionid = to.query.session_id
            if(sessionid) {
                await store.commit('pushCommand', new CheckSubscriptionBySessionIdCommand(sessionid as string))
            }
            next('/')
        }
    },
    {
        path: '/ridewithgpslinksuccess',
        beforeEnter: async function(to, from, next) {
            const returnPath = rideWithGpsLinkReturnPath(to.query.state)
            const error = (to.query.error_description ?? to.query.error) as string|null
            if(error) {
                await store.commit('pushCommand', new UserMessageCommand(error, 'error', false))
            } else if(to.query.code) {
                await store.commit('pushCommand', new LinkRideWithGpsCommand(to.query.code as string))
            } else {
                await store.commit('pushCommand', new UserMessageCommand('RideWithGPS authorization code invalid', 'error', false))
            }
            next(returnPath)
        }
    },
    {
        path: '/wahoolinksuccess',
        beforeEnter: async function(to, from, next) {
            console.info('[Wahoo link success] router received', {
                fullPath: to.fullPath,
                browserPathAndQuery: `${window.location.pathname}${window.location.search}`
            })

            const error = (to.query.error_description ?? to.query.error) as string|null
            if(error) {
                await store.commit('pushCommand', new UserMessageCommand(error, 'error', false))
            } else if(to.query.code) {
                const saveAfterLink = to.query.state === 'save-to-wahoo'
                    || WahooService.isSaveAfterLinkRequested()
                await store.commit('pushCommand', new LinkWahooCommand(
                    to.query.code as string,
                    saveAfterLink
                ))
            } else {
                await store.commit('pushCommand', new UserMessageCommand('Wahoo authorization code invalid', 'error', false))
            }
            next('/')
        }
    },
    {
        path: '/googlelogin',
        meta: {allowAnonymous: true},
        beforeEnter: async function(to, from, next) {
            if(to.query.error) {
                await store.commit('pushCommand', new UserMessageCommand(to.query.error as string, 'error', true));
                next('/')
            } else if(to.query?.code) {
                const state = to.query?.state as string|null;
                const googleLoginResponse = await LoginService.googleLogin(to.query.code as string);
                await store.commit('login', googleLoginResponse.data.user);
                await store.commit('loginAttempted');
                const rememberedReturnUrl = consumeLoginReturnUrl()
                const returnUrl = internalLoginReturnUrl(googleLoginResponse.data.returnUrl)
                    ?? rememberedReturnUrl
                    ?? '/'
                next(returnUrl)

                store.commit
            }
        }
    },
    {
        path: '/integrations',
        name: 'Integrations',
        component: () => import(/* webpackChunkName: "integrations" */ './Integrations.vue')
    },
    {
        path: '/cloud',
        name: 'Cloud storage',
        component: () => import(/* webpackChunkName: "cloud" */ './CloudStorage.vue')
    },
    {
        path: '/feedback/review/:id',
        name: 'Review feedback',
        component: () => import(/* webpackChunkName: "feedback-review" */ './FeedbackReview.vue'),
        meta: { doLogin: true }
    },
    {
        path: RideWithGpsMigrationPath,
        name: 'Migrate from RideWithGPS',
        component: () => import(/* webpackChunkName: "home" */ './Home.vue'),
        meta: { doLogin: true }
    },
    {
        path: '/w/:wahooRouteId',
        component: () => import(/* webpackChunkName: "home" */ './Home.vue'),
        beforeEnter(to, from, next) {
            next()
        },
        meta: {doLogin: true}
    },
    {
        path: '/:prefix(u|r)/:gpxUrl',
        component: () => import(/* webpackChunkName: "home" */ './Home.vue'),
        beforeEnter(to, from, next) {
            next()
        },
        meta: {doLogin: true}
    },
    {
        path: '*',
        beforeEnter: async function(to, from, next) {
            await store.commit('pushCommand', new UserMessageCommand('Path not found - showing home page instead.', 'error', false))
            next('/')
        }
    }
];

const docRoutes: Array<RouteConfig> = docPages.map(d => d.route)
const routes = appRoutes.concat(docRoutes)

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
});

const queryParamsToPreserve = new Set([
    'gclid',
    'gbraid',
    'wbraid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
]);

function sanitizedQuery(query: Record<string, unknown>) {
    return Object.fromEntries(
        Object.entries(query).filter(([key]) => queryParamsToPreserve.has(key))
    ) as Record<string, string | (string | null)[] | null | undefined>;
}

function queryNeedsSanitizing(query: Record<string, unknown>) {
    return Object.keys(query).some(key => !queryParamsToPreserve.has(key));
}

function rideWithGpsLinkReturnPath(state: unknown) {
    return state === RideWithGpsMigrationPath ? RideWithGpsMigrationPath : '/';
}

function consumeFlashMessageCookie() {
    const flashCookieName = 'FlashMessage=';
    const flashCookie = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(flashCookieName));

    if (!flashCookie) {
        return null;
    }

    const message = decodeURIComponent(flashCookie.substring(flashCookieName.length));
    document.cookie = 'FlashMessage=; Max-Age=0; Path=/';
    return message;
}

router.beforeEach(async (to, from, next) => {
    const flashMessage = consumeFlashMessageCookie();

    if(flashMessage === 'loginsuccess') {
        await store.commit('pushCommand', new UserMessageCommand('Logged in successfully', '', false))
    }

    if(flashMessage === 'notconfirmed') {
        await store.commit('pushCommand', new UserMessageCommand('You have not confirmed your account. Please check your email for a link, and re-register if necessary.', 'error', true))
    }

    if(flashMessage === 'notrecognized') {
        await store.commit('pushCommand', new UserMessageCommand('Username or password not recognized.', 'error', true))
    }

    if(to.query.clear === 'true') {
        localStorage.clear()
    }

    if(to.meta && to.meta.doLogin) {
        if(queryNeedsSanitizing(to.query)) {
            next({
                path: to.path,
                query: sanitizedQuery(to.query),
                hash: to.hash,
                replace: true
            })
            return
        }

        await LoginService.ensureLoginAttempted(store)

        PolicyService.getPolicy().then(policy => {
            store.commit('setPolicy', policy.data)
        }, reject => {
            console.warn('getPolicy failed: ', reject)
        })
        if(store.getters.claimsToBeSubscribed) {
            LoginService.checkSubscription().then(result => {
                if (result && result.data && !result.data.isValid) {
                    store.commit('setSubscriptionInvalid', result.data);
                }
            }, reject => {
                console.warn('checkSubscription failed: ', reject)
            })
        }

        next()
    } else {
        next() // (removing parameters breaks SetPassword) only remove ones that are consumed
    }

    
})

export default router
